import {Button} from '@dropbox/dig-components/buttons';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Split} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {CheckmarkLine, PinLine} from '@dropbox/dig-icons/assets';
import {pulseUserAtom} from 'atoms/auth';
import {makeSavedAiViewId, SavedAiView, savedAiViewsAtom} from 'atoms/savedAiViews';
import {snackbarAtom} from 'atoms/snackbar';
import {Layout} from 'components/DSYS/Layout';
import {Title} from 'components/DSYS/Title';
import {ROUTE_PATHS} from 'constant';
import {isSuperUser} from 'helpers/utils';
import {useDocumentTitle} from 'hooks/useDocumentTitle';
import {useAtom, useAtomValue, useSetAtom} from 'jotai';
import {useMemo} from 'react';
import {useNavigate, useSearchParams} from 'react-router';

import {RecipeMode} from './adminAiQueryApi';
import {RecipeReplay} from './RecipeReplay';

const parseMode = (raw: string | null): RecipeMode => (raw === 'snapshot' ? 'snapshot' : 'live');

/**
 * Toggle that pins the current view to (or unpins it from) the user's home
 * page. Persists to localStorage only — see {@link savedAiViewsAtom}.
 */
const SaveToHomeButton = ({
  encoded,
  mode,
  title,
}: {
  encoded: string;
  mode: RecipeMode;
  title: string;
}) => {
  const [savedViews, setSavedViews] = useAtom(savedAiViewsAtom);
  const setSnackbar = useSetAtom(snackbarAtom);

  const id = useMemo(() => makeSavedAiViewId(encoded, mode), [encoded, mode]);
  const isSaved = savedViews.some((v) => v.id === id);

  const toggle = () => {
    if (isSaved) {
      setSavedViews((prev) => prev.filter((v) => v.id !== id));
      setSnackbar({text: 'Removed from home'});
      return;
    }
    const next: SavedAiView = {id, title, recipe: encoded, mode, savedAt: Date.now()};
    setSavedViews((prev) => [next, ...prev.filter((v) => v.id !== id)]);
    setSnackbar({text: 'Saved to home'});
  };

  return (
    <Button
      variant={isSaved ? 'opacity' : 'primary'}
      onClick={toggle}
      withIconStart={<UIIcon src={isSaved ? CheckmarkLine : PinLine} />}
    >
      {isSaved ? 'Saved to home' : 'Save to home'}
    </Button>
  );
};

/**
 * Standalone replay of a shared recipe (the ?recipe= permalink opened from the
 * data agent). Re-runs the recipe live (or as-of, for snapshot) under the
 * current user's permissions and renders the resulting tables. Read-only.
 */
export const AdminAiView = () => {
  const pulseUser = useAtomValue(pulseUserAtom);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const encodedRecipe = searchParams.get('recipe');
  const mode = parseMode(searchParams.get('mode'));
  // Human label embedded by the share link (the question that produced the
  // recipe). Falls back to a generic heading for older/title-less links.
  const sharedTitle = searchParams.get('title')?.trim() || '';
  const heading = sharedTitle || 'Saved view';
  useDocumentTitle(sharedTitle || 'CompanyOS Data View');

  if (!pulseUser) {
    return null;
  }
  if (!isSuperUser(pulseUser)) {
    navigate('/');
    return null;
  }

  return (
    <Layout.Container
      breadcrumb={[
        {children: 'Admin', to: ROUTE_PATHS.ADMIN},
        {children: heading, to: ROUTE_PATHS.ADMIN_AI_VIEW},
      ]}
    >
      <Split alignY="center" gap="16" className={atoms({paddingTop: '4'})}>
        <Split.Item width="fill">
          <Title size={24}>
            {heading} <Text color="subtle">• {mode === 'snapshot' ? 'Snapshot' : 'Live data'}</Text>
          </Title>
        </Split.Item>
        {encodedRecipe && (
          <Split.Item width="hug">
            <SaveToHomeButton encoded={encodedRecipe} mode={mode} title={heading} />
          </Split.Item>
        )}
      </Split>

      <Box className={atoms({paddingTop: '20'})}>
        {!encodedRecipe ? (
          <Box
            padding="12"
            borderRadius="Medium"
            className={atoms({backgroundColor: 'Background Subtle'})}
          >
            <Text color="error">No recipe provided in the link.</Text>
          </Box>
        ) : (
          <RecipeReplay encoded={encodedRecipe} mode={mode} />
        )}
      </Box>
    </Layout.Container>
  );
};
